import { App as AntdApp, ConfigProvider, Spin as Loader } from 'antd';
import type { Locale } from 'antd/es/locale';
import enUS from 'antd/locale/en_US';
import React from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';

import PageError from 'components/common/PageError';
import Toast from 'components/Toast';
import MainLayout from 'components/ui/layout/MainLayout';
import PublicLayout from 'components/ui/layout/PublicLayout';
import useAnalytics from 'config/analytics';
import antdTheme from 'config/theme/antd';
import antdComponents from 'config/theme/antdComponents';
import { getLocaleUsage, ISO_DEFAULT_LANGUAGE } from 'constants/LanguageConstants';
import { useSelector } from 'hooks/useSelector';
import { isAppLoading, hasAppError as hasAppErrorSelector } from 'modules/app/selectors';
import { getLocale, getTranslationsForCurrentLocale } from 'modules/i18n/selectors/data';
import { getCurrentUserAdditionalData, haveInitializeSession } from 'modules/users/selectors/currentUser';
import { getSelectedDealerTimeZoneOffset } from 'selectors/dealerSelectors';
import { setDateLocale as setDateFnsLocale } from 'utils/date';

import ErrorHandler from './components/error/ErrorHandler';

/**
 * `__setDefaultTimeZone` is a non-standard escape hatch some browsers expose to pin the default
 * time zone; it is not part of the `Intl` lib types, hence the local shape.
 */
type DateTimeFormatWithDefaultTimeZone = typeof Intl.DateTimeFormat & {
  __setDefaultTimeZone: (timeZone: string) => void;
};

const loadLocales = async (antDLocale: string): Promise<{ default: Locale }> =>
  import(`antd/locale/${antDLocale}.js`).catch(() => ({ default: enUS }));

const App: React.FC = () => {
  useAnalytics();
  const [antLocale, setAntLocale] = React.useState<Locale>(enUS);
  const locale = useSelector(getLocale);
  const isLoading = useSelector(isAppLoading);
  const hasAppError = useSelector(hasAppErrorSelector);
  const user = useSelector(getCurrentUserAdditionalData);
  const isSessionInit = useSelector(haveInitializeSession);
  const dealerTimezone = useSelector(getSelectedDealerTimeZoneOffset);
  const translations = useSelector(getTranslationsForCurrentLocale);

  const isLoaded = user !== undefined;

  const userLocaleVariant = React.useMemo(() => getLocaleUsage(locale).i18n, [locale]);

  React.useLayoutEffect(() => {
    if (locale) {
      setDateFnsLocale(getLocaleUsage(locale).date);
    }

    // Set timezone on browsers that do not have a default one
    if (dealerTimezone && '__setDefaultTimeZone' in Intl.DateTimeFormat) {
      // eslint-disable-next-line no-underscore-dangle
      (Intl.DateTimeFormat as DateTimeFormatWithDefaultTimeZone).__setDefaultTimeZone(dealerTimezone);
    }

    (async function getAntDLocale() {
      const ant = await loadLocales(getLocaleUsage(locale).ui);
      setAntLocale(ant.default);
    }());
  }, [locale, dealerTimezone]);

  return (
    <>
      {isLoading && <Loader />}
      {!isLoading && (
        <ConfigProvider theme={antdTheme} locale={antLocale} {...antdComponents}>
          <IntlProvider
            textComponent="span"
            messages={translations}
            locale={userLocaleVariant}
            defaultLocale={ISO_DEFAULT_LANGUAGE}
            timeZone={dealerTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
          >
            {/* antd's own default is 4.5s. `placement` is not set: `topRight` is already antd's
                (`notification/useNotification.js:18`). This cannot live in `antdComponents` —
                `ConfigProviderProps.notification` carries only `closeIcon`/`classNames`/`styles`,
                while `duration` belongs to `AppProps.notification`. */}
            <AntdApp className="h-100" notification={{ duration: 6 }}>
              {hasAppError && (
                <PageError
                  title={<FormattedMessage id="page.error.apiError.title" defaultMessage="We're on it!" />}
                  message={
                    <FormattedMessage
                      id="page.error.apiError.description"
                      defaultMessage="Something’s gone temporarily sideways, and the app isn’t responding as it should. Our team is actively working on fixing the issue, and we’re doing everything we can to get things back up and running smoothly. Please check back a bit later — we appreciate your patience and understanding."
                    />
                  }
                />
              )}
              {!hasAppError && isSessionInit && (
                <ErrorHandler>
                  <>
                    {!isLoaded && <PublicLayout />}
                    {isLoaded && <MainLayout />}
                  </>
                  <Toast />
                </ErrorHandler>
              )}
            </AntdApp>
          </IntlProvider>
        </ConfigProvider>
      )}
    </>
  );
};

export default App;
